import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useTracking } from '@/hooks/useTracking';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

import { extractWorkspaceId } from '../dashboard/useCalculateChurn';

export interface CreateTicketFromSignalResponse {
  message: string;
  ticket: {
    id: string;
    title: string;
    description: string;
    type: string;
    priority: string;
    status: string;
    columnId?: string | null;
    metadata?: Record<string, unknown>;
  };
}

async function postCreateTicketFromSignal(workspaceId: string, signalId: string, token: string | undefined) {
  const base = getBackendBaseUrl();
  const headers = backendRequestHeaders(token);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const { data } = await axios.post<CreateTicketFromSignalResponse>(
    `${base}/workspaces/${workspaceId}/churn-signals/${signalId}/create-ticket`,
    {},
    { headers },
  );

  return data;
}

export function useCreateTicketFromSignal() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { trackEvent } = useTracking();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = extractWorkspaceId(workspace);
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const bearer = resolveBearerToken(sessionJwt);

  return useMutation({
    mutationFn: (signalId: string) => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      return postCreateTicketFromSignal(workspaceId, signalId, bearer || undefined);
    },
    onSuccess: async (res, signalId) => {
      trackEvent('ticket_created_from_signal', {
        signal_id: signalId,
        ticket_id: res.ticket?.id,
        ticket_type: res.ticket?.type,
      });
      // Refetch inactive queries too — board may not be mounted (dashboard / member profile).
      await queryClient.invalidateQueries([queryKeys.tickets, workspaceId], { refetchInactive: true });
    },
  });
}
